/**
 * ConnectionsPanel — Communications → Connections (SUN-70, mockups/kiaan-connections.html).
 *
 * Left: how you reach Kiaan. Right: how the agents are connected.
 * Status comes from real data: the web widget is where you are; Kiaan Work and every agent's
 * dot come from GET /kiaan/agents. Phone app, Telegram and WhatsApp reports have no AURA side
 * yet, so they say "Not set up". Set-up buttons are admin only and say "Coming soon" for now.
 */
import { useEffect, useState } from 'react';
import { listAgents, type KiaanAgent } from './kiaanApi';

type St = 'on' | 'off' | 'apart' | 'down' | 'checking';
const ST_TEXT: Record<St, string> = { on: 'Connected', off: 'Not set up', apart: 'Kept apart', down: 'Not reachable', checking: 'Checking…' };
const ST_CLASS: Record<St, string> = { on: 'kw-ok', off: 'kw-no', apart: 'kw-bad', down: 'kw-bad', checking: 'kw-wip' };
const DOT: Record<string, string> = { working: 'kw-b', ready: 'kw-g', off: 'kw-o' };

interface Row { icon: string; title: string; detail: string; st: St; setUp?: string }

export function ConnectionsPanel({ isAdmin, onOpenChannels }: { isAdmin: boolean; onOpenChannels?: () => void }) {
  const [agents, setAgents] = useState<KiaanAgent[] | null>(null);
  const [hub, setHub] = useState<St>('checking');

  useEffect(() => {
    let alive = true;
    listAgents()
      .then((list) => { if (alive) { setAgents(list); setHub('on'); } })
      .catch(() => { if (alive) { setAgents([]); setHub('down'); } });
    return () => { alive = false; };
  }, []);

  const rows: Row[] = [
    { icon: '🖥', title: 'AURA on the web', detail: 'This widget · all agents · Sun Stellar data', st: 'on' },
    { icon: '🧭', title: 'Kiaan Work', detail: 'Where agents get their tasks', st: hub },
    { icon: '📱', title: 'Your phone — AURA app', detail: 'Alerts: "Needs you", task done, daily report', st: 'off', setUp: 'Set up' },
    { icon: '✈️', title: 'Telegram bot', detail: "Chat with Kiaan + agents from Telegram · on Sun Stellar's own server", st: 'off', setUp: 'Connect' },
    { icon: '🟢', title: 'WhatsApp (ChatKart)', detail: 'Reports and "Needs you" on WhatsApp · your own number', st: 'off', setUp: 'Connect' },
    { icon: '🤖', title: 'Your other bots', detail: 'Central Kiaan bot — kept separate: Sun Stellar data never goes there', st: 'apart' },
  ];
  const team = (agents ?? []).filter((a) => !a.main);

  return (
    <div className="kw-grid kw-conn" data-testid="comms-connections">
      <div className="kw-box">
        <h4>How you reach Kiaan</h4>
        {rows.map((r) => (
          <div key={r.title} className="kw-conn-row" data-testid="conn-row">
            <span className="kw-conn-ic" aria-hidden="true">{r.icon}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <b>{r.title}</b>
              <div className="kw-small">{r.detail}</div>
            </div>
            <span className={`kw-pill ${ST_CLASS[r.st]}`} data-testid="conn-status">{ST_TEXT[r.st]}</span>
            {r.setUp && isAdmin && (
              <button type="button" className="kw-btn kw-light" disabled title="Coming soon" data-testid="conn-setup">
                {r.setUp} · Coming soon
              </button>
            )}
          </div>
        ))}
        <div className="kw-sec" style={{ marginTop: 12 }}>What Kiaan sends you</div>
        <div className="kw-small">⚑ Needs you · ✅ Task finished + checked — on the web, in Live work, as it happens.</div>
      </div>

      <div className="kw-box">
        <h4>How the agents are connected</h4>
        <div className="kw-map" data-testid="agent-map">
          <div className="kw-map-node kw-map-hub">
            <b>Kiaan Work</b>
            <span className="kw-small">tasks hub · {ST_TEXT[hub].toLowerCase()}</span>
          </div>
          <div className="kw-map-line kw-small">assigns tasks ↓ · notes on each other's tasks ↔</div>
          <div className="kw-map-row">
            {agents === null && <span className="kw-small">Loading…</span>}
            {agents !== null && team.length === 0 && <span className="kw-small">No agents yet.</span>}
            {team.map((a) => (
              <div key={a.id} className="kw-map-node" data-testid="map-agent">
                <b>{a.name}</b>
                <span className="kw-small"><i className={`kw-st ${DOT[a.status ?? 'off']}`} /> {a.status ?? 'off'}</span>
              </div>
            ))}
          </div>
          <div className="kw-map-line kw-small">
            talk in channels (@tags) ↔
            {onOpenChannels && (
              <>
                {' · '}
                <button type="button" className="kw-btn kw-light" onClick={onOpenChannels}>Talk in channels</button>
              </>
            )}
          </div>
          <div className="kw-map-line kw-small">reviews ↑</div>
          <div className="kw-map-node">
            <b>You + Claude</b>
            <span className="kw-small">review · approve · ship</span>
          </div>
        </div>
        <div className="kw-small" style={{ marginTop: 8 }} data-testid="agent-talk-line">
          Agents talk through tasks and notes in Kiaan Work, and in Channels: tag one with @Name and it answers there or hands the work to another agent.
        </div>
      </div>
    </div>
  );
}
